import {
  type CmmdProject,
  type CmmdTask,
  type CmmdWorkspace,
  type KeybindingCommand,
} from "@cmmd-forge/contracts";
import type { SidebarThreadSortOrder } from "@cmmd-forge/contracts/settings";
import { type ReactNode } from "react";
import type { GitHubRepo } from "../hooks/useGitHubRepos";
import type { ProjectImportDestination, ProjectImportDestinationKind } from "../projectImportStore";
import type { ForgeRuntimeSurface } from "../environments/runtimeSurface";
import { sortThreads } from "../lib/threadSort";
import { formatRelativeTimeLabel } from "../timestampFormat";
import { type Project, type SidebarThreadSummary, type Thread } from "../types";
import type { WorkItem } from "./homebase/unifiedWorkItems";
import { getStatusConfig } from "./command-center/statusConfig";
import { localDeviceNoun } from "../lib/utils";
import { formatProductProjectLabel, formatProductWorkspacePath } from "~/productSafeLabels";

export const RECENT_THREAD_LIMIT = 12;
export const ITEM_ICON_CLASS = "size-4 text-muted-foreground/80";
export const ADDON_ICON_CLASS = "size-4";

export interface CommandPaletteItem {
  readonly kind: "action" | "submenu";
  readonly value: string;
  readonly searchTerms: ReadonlyArray<string>;
  readonly title: ReactNode;
  readonly description?: string;
  readonly badge?: string;
  readonly disabled?: boolean;
  readonly timestamp?: string;
  readonly icon: ReactNode;
  readonly shortcutCommand?: KeybindingCommand;
}

export interface CommandPaletteActionItem extends CommandPaletteItem {
  readonly kind: "action";
  readonly keepOpen?: boolean;
  readonly run: () => Promise<void>;
}

export interface CommandPaletteSubmenuItem extends CommandPaletteItem {
  readonly kind: "submenu";
  readonly addonIcon: ReactNode;
  readonly groups: ReadonlyArray<CommandPaletteGroup>;
  readonly initialQuery?: string;
}

export interface CommandPaletteGroup {
  readonly value: string;
  readonly label: string;
  readonly items: ReadonlyArray<CommandPaletteActionItem | CommandPaletteSubmenuItem>;
}

export interface CommandPaletteView {
  readonly addonIcon: ReactNode;
  readonly groups: ReadonlyArray<CommandPaletteGroup>;
  readonly initialQuery?: string;
  readonly mode?: "empty-project" | "cmmd-project" | "github" | "local-folder";
  readonly importDestination?: ProjectImportDestination;
}

export type CommandPaletteMode = "root" | "submenu";

export interface AddProjectSourceDescriptor {
  readonly id: "empty-project" | "cmmd-project" | "github" | "local-folder";
  readonly value: string;
  readonly searchTerms: readonly string[];
  readonly title: string;
  readonly description: string;
  readonly badge?: string;
  readonly disabled?: boolean;
  /** Why it is disabled, and therefore what the user could change. */
  readonly disabledReason?: string;
}

export type AddProjectSourceMode = NonNullable<CommandPaletteView["mode"]>;
export type AddProjectSourceIconName =
  | "folder"
  | "command"
  | "github"
  | "link"
  | "monitor"
  | "folder-plus";

export interface ProjectSourceSubmitState {
  readonly visible: boolean;
  readonly disabled: boolean;
  readonly ariaLabel: string;
  readonly title: string;
}

const PROJECT_IMPORT_DESTINATIONS: readonly ProjectImportDestinationKind[] = [
  "forge-cloud",
  "desktop-local",
];

export function buildAddProjectRootDescriptor(): {
  readonly title: string;
  readonly searchTerms: readonly string[];
} {
  return {
    title: "Add project",
    searchTerms: [
      "add project",
      "add project",
      "source",
      "sources",
      "github repository",
      "git url",
      "new cloud project",
      "new cloud project",
      "empty project",
      "blank project",
      "empty project",
      "blank project",
      "local folder",
      "clone",
      "import",
    ],
  };
}

export function buildAddProjectSourceDescriptors(
  destination?: ProjectImportDestinationKind | null,
  surface?: ForgeRuntimeSurface,
  /** Injectable so a test asserting device copy is not host-dependent. */
  platform?: string,
): AddProjectSourceDescriptor[] {
  const supportsLocalFolder = surface === "desktop-local" || surface === "desktop-cloud";
  const isCloud =
    destination === "forge-cloud" ||
    (surface === "cloud-official" && destination !== "desktop-local");
  const isLocal = destination === "desktop-local";
  const descriptors: AddProjectSourceDescriptor[] = [
    {
      id: "cmmd-project",
      value: "action:add-project:cmmd-project",
      searchTerms: ["cmmd", "project", "planning", "command", "github", "repository", "repo"],
      title: "CMMD Project",
      description: "Start from an existing CMMD project.",
    },
    {
      id: "github",
      value: "action:add-project:github",
      searchTerms: ["github", "repository", "repo", "clone", "owner repo"],
      title: "GitHub Repository",
      description: "Import from GitHub.",
    },
    {
      id: "empty-project",
      value: "action:add-project:new-cloud",
      searchTerms: [
        "create new project",
        "new project",
        "workspace",
        "blank",
        "empty",
        "new",
        "cloud",
        "scratch",
        "folder",
        "forge cloud",
      ],
      title: "Create New",
      description: isCloud
        ? "Create in CMMD and Forge Cloud"
        : isLocal
          ? `Create in CMMD and on ${localDeviceNoun(platform)}`
          : "Create in CMMD and Forge",
    },
    {
      id: "local-folder",
      value: "action:add-project:local-folder",
      searchTerms: ["local", "folder", "directory", "browse", "disk", "device"],
      title: "Select Folder",
      description: `Runs on ${localDeviceNoun(platform)}`,
    },
  ];
  // Two different reasons this source may not be usable, and they deserve two
  // different treatments.
  //
  // A cloud workspace has no local folder to point at, so the source does not
  // APPLY — it is removed, because a disabled row would imply some setting
  // could enable it.
  // Cloud-official has no local device in the picture at all, so it takes the
  // same treatment: "open the desktop app" would be advice its user cannot act on.
  if (destination === "forge-cloud" || surface === "cloud-official") {
    return descriptors.filter((descriptor) => descriptor.id !== "local-folder");
  }
  // A browser has no native file picker, so the source applies but is not
  // available HERE. Silently hiding it leaves someone hunting for a row that is
  // not there; showing it disabled names what to change instead.
  if (!supportsLocalFolder) {
    // Only worth explaining when the user has actually asked for this device.
    // With no destination chosen there is nothing to explain yet, so it stays
    // out of the list rather than adding a disabled row to a menu of options.
    return destination === "desktop-local"
      ? descriptors.map((descriptor) =>
          descriptor.id === "local-folder"
            ? {
                ...descriptor,
                disabled: true,
                disabledReason: "Open Forge in the desktop app to pick a folder on this device.",
              }
            : descriptor,
        )
      : descriptors.filter((descriptor) => descriptor.id !== "local-folder");
  }
  return descriptors;
}

export function getAddProjectSourceMode(
  id: AddProjectSourceDescriptor["id"],
): AddProjectSourceMode {
  return id;
}

export function getDefaultProjectImportDestination(
  mode: AddProjectSourceMode,
  lockedDestination?: ProjectImportDestinationKind | null,
): ProjectImportDestinationKind {
  if (lockedDestination) return lockedDestination;
  return mode === "local-folder" ? "desktop-local" : "forge-cloud";
}

export function getAllowedProjectImportDestinations(
  mode: AddProjectSourceMode,
  lockedDestination?: ProjectImportDestinationKind | null,
  surface?: ForgeRuntimeSurface,
): readonly ProjectImportDestinationKind[] {
  if (lockedDestination) {
    return [lockedDestination];
  }
  if (mode === "local-folder") {
    return [getDefaultProjectImportDestination(mode)];
  }
  if (surface === "cloud-official") {
    return PROJECT_IMPORT_DESTINATIONS.filter((destination) => destination !== "desktop-local");
  }
  return PROJECT_IMPORT_DESTINATIONS;
}

export function getAddProjectSourceIconName(
  id: AddProjectSourceDescriptor["id"],
): AddProjectSourceIconName {
  switch (id) {
    case "empty-project":
      return "folder-plus";
    case "cmmd-project":
      return "command";
    case "github":
      return "github";
    case "local-folder":
      return "monitor";
  }
}

export function getProjectSourceSubmitLabel(input: {
  readonly isCreatingEmptyProject: boolean;
  readonly isAddingSource: boolean;
}): "Add" | "Adding" {
  return input.isCreatingEmptyProject || input.isAddingSource ? "Adding" : "Add";
}

export function getCommandPaletteInputPlaceholderForView(input: {
  readonly mode: CommandPaletteMode;
  readonly currentView: CommandPaletteView | null;
}): string {
  switch (input.currentView?.mode) {
    case "empty-project":
      return "Project name";
    case "cmmd-project":
      return "Search CMMD projects";
    case "github":
      return "Search repositories";
    case "local-folder":
    case undefined:
      return getCommandPaletteInputPlaceholder(input.mode);
  }
}

export function getProjectSourceSubmitState(input: {
  readonly currentView: CommandPaletteView | null;
  readonly query: string;
  readonly isCreatingEmptyProject: boolean;
  readonly isAddingSource: boolean;
}): ProjectSourceSubmitState {
  if (input.currentView?.mode === "empty-project") {
    return {
      visible: true,
      disabled: input.query.trim().length === 0 || input.isCreatingEmptyProject,
      ariaLabel: "Add blank project",
      title: "Add blank project",
    };
  }

  return {
    visible: false,
    disabled: true,
    ariaLabel: "",
    title: "",
  };
}

export function resolveEmptyProjectEnvironmentId<T>(primaryEnvironmentId: T | null): T | null {
  return primaryEnvironmentId;
}

function normalizeSearchText(value: string): string {
  return value.trim().toLowerCase().replace(/\s+/g, " ");
}

export function filterPickerRows<T>(input: {
  readonly rows: ReadonlyArray<T>;
  readonly query: string;
  readonly getSearchText: (row: T) => string;
}): T[] {
  const normalizedQuery = normalizeSearchText(input.query);
  if (normalizedQuery.length === 0) {
    return [...input.rows];
  }

  return input.rows.filter((row) =>
    normalizeSearchText(input.getSearchText(row)).includes(normalizedQuery),
  );
}

/**
 * Shared searchable text for a CMMD project row, used by every picker that
 * lets a user filter CMMD projects by name/workspace/team (the CMMD-project
 * source picker and the GitHub-import planning-context picker).
 */
export function cmmdProjectSearchText(project: CmmdProject): string {
  return [
    project.title,
    // Both names, so a project renamed in Forge is still findable by the name
    // the board uses — and vice versa.
    project.shortName ?? "",
    project.workspaceName ?? "",
    project.teamName ?? "",
    resolveGitHubRepositoryIdentity(project.githubRepoUrl)?.label ?? "",
  ].join(" ");
}

export interface GitHubRepositoryIdentity {
  readonly label: string;
  readonly url: string;
}

function isValidGitHubRepositorySegment(segment: string): boolean {
  return /^[a-z0-9_.-]+$/i.test(segment);
}

export function resolveGitHubRepositoryIdentity(
  githubRepoUrl: string | null | undefined,
): GitHubRepositoryIdentity | null {
  const value = githubRepoUrl?.trim();
  if (!value) return null;

  const scpMatch = /^git@github\.com:([^/]+)\/(.+)$/i.exec(value);
  let owner = scpMatch?.[1];
  let repository = scpMatch?.[2];

  if (!owner || !repository) {
    try {
      const parsed = new URL(value);
      if (parsed.hostname.toLowerCase() !== "github.com") return null;
      const segments = parsed.pathname.split("/").filter(Boolean);
      if (segments.length !== 2) return null;
      owner = segments[0];
      repository = segments[1];
    } catch {
      return null;
    }
  }
  if (!owner || !repository) return null;

  const normalizedRepository = repository.replace(/\.git$/i, "");
  if (
    !isValidGitHubRepositorySegment(owner) ||
    !isValidGitHubRepositorySegment(normalizedRepository)
  ) {
    return null;
  }

  return {
    label: `${owner}/${normalizedRepository}`,
    url: `https://github.com/${owner}/${normalizedRepository}`,
  };
}

export function resolveCmmdProjectCampaignName(
  project: CmmdProject,
  campaigns: readonly { readonly id: string; readonly title: string }[],
): string | null {
  return (
    campaigns.find((campaign) => campaign.id === project.campaignId)?.title ??
    project.campaignTitle ??
    null
  );
}

export interface CmmdProjectWorkspaceGroup {
  readonly workspaceId: string;
  readonly workspaceName: string;
  readonly workspace: CmmdWorkspace | null;
  readonly projects: readonly CmmdProject[];
}

function normalizeCmmdWorkspaceId(value: string | number | null | undefined): string | null {
  if (typeof value === "number") return Number.isFinite(value) ? String(value) : null;
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function cmmdWorkspaceFallbackId(name: string): string {
  return `workspace-name:${name.trim().toLowerCase()}`;
}

export function groupCmmdProjectsByWorkspace(input: {
  readonly projects: readonly CmmdProject[];
  readonly workspaces: readonly CmmdWorkspace[];
}): readonly CmmdProjectWorkspaceGroup[] {
  const workspaceById = new Map<string, CmmdWorkspace>();
  const workspaceByName = new Map<string, CmmdWorkspace>();
  for (const workspace of input.workspaces) {
    const workspaceId = normalizeCmmdWorkspaceId(workspace.id);
    if (workspaceId) workspaceById.set(workspaceId, workspace);
    workspaceByName.set(workspace.name.trim().toLowerCase(), workspace);
  }

  const groups = new Map<string, CmmdProjectWorkspaceGroup & { projects: CmmdProject[] }>();
  for (const project of input.projects) {
    const explicitWorkspaceId = normalizeCmmdWorkspaceId(project.workspaceId);
    const workspaceByExplicitId = explicitWorkspaceId
      ? workspaceById.get(explicitWorkspaceId)
      : null;
    const workspaceByProjectName = project.workspaceName
      ? workspaceByName.get(project.workspaceName.trim().toLowerCase())
      : null;
    const workspace = workspaceByExplicitId ?? workspaceByProjectName ?? null;
    const workspaceName = workspace?.name ?? project.workspaceName ?? "Workspace";
    const workspaceId =
      explicitWorkspaceId ??
      normalizeCmmdWorkspaceId(workspace?.id) ??
      cmmdWorkspaceFallbackId(workspaceName);
    const existing = groups.get(workspaceId);
    if (existing) {
      existing.projects.push(project);
    } else {
      groups.set(workspaceId, {
        workspaceId,
        workspaceName,
        workspace,
        projects: [project],
      });
    }
  }

  return [...groups.values()].toSorted((a, b) => a.workspaceName.localeCompare(b.workspaceName));
}

function normalizeRepoKey(value: string): string {
  return value
    .trim()
    .replace(/^https?:\/\/github\.com\//i, "")
    .replace(/^git@github\.com:/i, "")
    .replace(/\.git$/i, "")
    .replace(/^github\.com\//i, "")
    .toLowerCase();
}

function projectMatchesGitHubRepo(project: Project, repo: GitHubRepo): boolean {
  const repoKey = normalizeRepoKey(repo.full_name);
  const identity = project.repositoryIdentity;
  if (identity) {
    const candidates = [
      identity.canonicalKey,
      identity.displayName,
      identity.owner && identity.name ? `${identity.owner}/${identity.name}` : null,
      identity.locator.remoteUrl,
    ];
    if (candidates.some((candidate) => candidate && normalizeRepoKey(candidate) === repoKey)) {
      return true;
    }
  }

  const repoName = repo.full_name.split("/").at(-1)?.toLowerCase();
  const projectFolder = project.cwd.split(/[\\/]/).findLast(Boolean)?.toLowerCase();
  return Boolean(repoName && projectFolder && repoName === projectFolder);
}

export function findImportedGitHubProject(
  projects: readonly Project[],
  repo: GitHubRepo,
): Project | null {
  return projects.find((project) => projectMatchesGitHubRepo(project, repo)) ?? null;
}

export function findImportedGitHubProjectForEnvironment(
  projects: readonly Project[],
  repo: GitHubRepo,
  environmentId: Project["environmentId"] | null,
): Project | null {
  if (!environmentId) return null;
  return (
    projects.find(
      (project) =>
        project.environmentId === environmentId && projectMatchesGitHubRepo(project, repo),
    ) ?? null
  );
}

export function buildProjectActionItems(input: {
  projects: ReadonlyArray<Project>;
  valuePrefix: string;
  icon: (project: Project) => ReactNode;
  runProject: (project: Project) => Promise<void>;
  /**
   * The project's Forge display name, when a linked CMMD project has one.
   * Optional so callers without the CMMD join (tests, other palettes) keep the
   * checkout label — the same fallback `resolveProjectDisplayName` returns.
   */
  resolveDisplayName?: (project: Project) => string | undefined;
}): CommandPaletteActionItem[] {
  return input.projects.map((project) => ({
    kind: "action",
    value: `${input.valuePrefix}:${project.environmentId}:${project.id}`,
    // Both names stay searchable, so choosing a display name in Forge cannot
    // make a project unfindable by the name its checkout still carries.
    searchTerms: [project.name, input.resolveDisplayName?.(project) ?? "", project.cwd],
    title: formatProductProjectLabel(input.resolveDisplayName?.(project) ?? project.name),
    description: formatProductWorkspacePath(project.cwd),
    icon: input.icon(project),
    run: async () => {
      await input.runProject(project);
    },
  }));
}

export function buildThreadActionItems(input: {
  threads: ReadonlyArray<
    Pick<
      SidebarThreadSummary,
      "archivedAt" | "branch" | "createdAt" | "environmentId" | "id" | "projectId" | "title"
    > & {
      updatedAt?: string | undefined;
      latestUserMessageAt?: string | null;
    }
  >;
  activeThreadId?: Thread["id"];
  projectTitleById: ReadonlyMap<Project["id"], string>;
  sortOrder: SidebarThreadSortOrder;
  icon: ReactNode;
  runThread: (thread: Pick<SidebarThreadSummary, "environmentId" | "id">) => Promise<void>;
  limit?: number;
}): CommandPaletteActionItem[] {
  const sortedThreads = sortThreads(
    input.threads.filter((thread) => thread.archivedAt === null),
    input.sortOrder,
  );
  const visibleThreads =
    input.limit === undefined ? sortedThreads : sortedThreads.slice(0, input.limit);

  return visibleThreads.map((thread) => {
    const projectTitle = input.projectTitleById.get(thread.projectId);
    const descriptionParts: string[] = [];

    if (projectTitle) {
      descriptionParts.push(projectTitle);
    }
    if (thread.branch) {
      descriptionParts.push(`#${thread.branch}`);
    }
    if (thread.id === input.activeThreadId) {
      descriptionParts.push("Current thread");
    }

    return {
      kind: "action",
      value: `thread:${thread.id}`,
      searchTerms: [thread.title, projectTitle ?? "", thread.branch ?? ""],
      title: thread.title,
      description: descriptionParts.join(" · "),
      timestamp: formatRelativeTimeLabel(
        thread.latestUserMessageAt ?? thread.updatedAt ?? thread.createdAt,
      ),
      icon: input.icon,
      run: async () => {
        await input.runThread(thread);
      },
    };
  });
}

export function buildCmmdTaskActionItems(input: {
  tasks: ReadonlyArray<WorkItem>;
  icon: ReactNode;
  runTask: (task: WorkItem) => Promise<void>;
}): CommandPaletteActionItem[] {
  return input.tasks.map((task) => ({
    kind: "action",
    value: `cmmd-task:${task.id}`,
    searchTerms: [task.title, task.projectName, task.campaignName ?? "", task.workspaceName ?? ""],
    title: task.title,
    description: [task.projectName, getStatusConfig(task.status).label].filter(Boolean).join(" · "),
    icon: input.icon,
    run: async () => {
      await input.runTask(task);
    },
  }));
}

export function convertCmmdApiTaskToWorkItem(input: {
  task: CmmdTask;
  fallbackActivityAt: string;
}): WorkItem {
  const { task, fallbackActivityAt } = input;
  return {
    id: `cmmd:${task.id}`,
    title: task.title,
    source: "cmmd-task",
    status: task.status,
    priority: task.priority,
    projectName: task.projectId ?? "",
    projectId: null,
    campaignName: null,
    workspaceId: task.workspaceId ?? null,
    workspaceName: task.workspaceName ?? null,
    teamId: task.teamId ?? null,
    teamName: task.teamName ?? null,
    assigneeId: task.assigneeId ?? null,
    dueDate: task.dueDate,
    agentStatus: "idle",
    agentProvider: null,
    environmentId: null,
    threadId: null,
    activityAt: task.updatedAt ?? fallbackActivityAt,
    hasActionableProposedPlan: false,
    needsAttention: false,
    dependsOn: [],
    blockedByCount: 0,
    isReady: false,
  };
}

export function buildUncachedCmmdApiWorkItems(input: {
  apiTasks: ReadonlyArray<CmmdTask>;
  cachedTaskValueSet: ReadonlySet<string>;
  fallbackActivityAt: string;
}): WorkItem[] {
  return input.apiTasks
    .filter((task) => !input.cachedTaskValueSet.has(`cmmd-task:cmmd:${task.id}`))
    .map((task) =>
      convertCmmdApiTaskToWorkItem({
        task,
        fallbackActivityAt: input.fallbackActivityAt,
      }),
    );
}

function rankSearchFieldMatch(field: string, normalizedQuery: string): number {
  const normalizedField = normalizeSearchText(field);
  if (normalizedField.length === 0 || !normalizedField.includes(normalizedQuery)) {
    return Number.NEGATIVE_INFINITY;
  }
  if (normalizedField === normalizedQuery) {
    return 3;
  }
  if (normalizedField.startsWith(normalizedQuery)) {
    return 2;
  }
  return 1;
}

function rankCommandPaletteItemMatch(
  item: CommandPaletteActionItem | CommandPaletteSubmenuItem,
  normalizedQuery: string,
): number {
  const terms = item.searchTerms.filter((term) => term.length > 0);
  if (terms.length === 0) {
    return 0;
  }

  for (const [index, field] of terms.entries()) {
    const fieldRank = rankSearchFieldMatch(field, normalizedQuery);
    if (fieldRank !== Number.NEGATIVE_INFINITY) {
      return 1_000 - index * 100 + fieldRank;
    }
  }

  return 0;
}

export function filterCommandPaletteGroups(input: {
  activeGroups: ReadonlyArray<CommandPaletteGroup>;
  query: string;
  isInSubmenu: boolean;
  projectSearchItems: ReadonlyArray<CommandPaletteActionItem>;
  threadSearchItems: ReadonlyArray<CommandPaletteActionItem>;
  cmmdTaskSearchItems?: ReadonlyArray<CommandPaletteActionItem>;
}): CommandPaletteGroup[] {
  const isActionsFilter = input.query.startsWith(">");
  const searchQuery = isActionsFilter ? input.query.slice(1) : input.query;
  const normalizedQuery = normalizeSearchText(searchQuery);

  if (normalizedQuery.length === 0) {
    if (isActionsFilter) {
      return input.activeGroups.filter((group) => group.value === "actions");
    }
    return [...input.activeGroups];
  }

  let baseGroups = [...input.activeGroups];
  if (isActionsFilter) {
    baseGroups = baseGroups.filter((group) => group.value === "actions");
  } else if (!input.isInSubmenu) {
    baseGroups = baseGroups.filter((group) => group.value !== "recent-threads");
  }

  const searchableGroups = [...baseGroups];
  if (!input.isInSubmenu && !isActionsFilter) {
    if (input.projectSearchItems.length > 0) {
      searchableGroups.push({
        value: "projects-search",
        label: "Workspaces",
        items: input.projectSearchItems,
      });
    }
    if (input.threadSearchItems.length > 0) {
      searchableGroups.push({
        value: "threads-search",
        label: "Threads",
        items: input.threadSearchItems,
      });
    }
    if (input.cmmdTaskSearchItems && input.cmmdTaskSearchItems.length > 0) {
      searchableGroups.push({
        value: "cmmd-tasks-search",
        label: "CMMD Tasks",
        items: input.cmmdTaskSearchItems,
      });
    }
  }

  return searchableGroups.flatMap((group) => {
    const items = group.items
      .map((item, index) => {
        const haystack = normalizeSearchText(item.searchTerms.join(" "));
        if (!haystack.includes(normalizedQuery)) {
          return null;
        }

        return {
          item,
          index,
          rank: rankCommandPaletteItemMatch(item, normalizedQuery),
        };
      })
      .filter(
        (entry): entry is { item: (typeof group.items)[number]; index: number; rank: number } =>
          entry !== null,
      )
      .toSorted((left, right) => right.rank - left.rank || left.index - right.index)
      .map((entry) => entry.item);

    if (items.length === 0) {
      return [];
    }

    return [{ value: group.value, label: group.label, items }];
  });
}

export function getCommandPaletteMode(input: {
  currentView: CommandPaletteView | null;
}): CommandPaletteMode {
  return input.currentView ? "submenu" : "root";
}

export function buildRootGroups(input: {
  actionItems: ReadonlyArray<CommandPaletteActionItem | CommandPaletteSubmenuItem>;
  recentThreadItems: ReadonlyArray<CommandPaletteActionItem>;
}): CommandPaletteGroup[] {
  const groups: CommandPaletteGroup[] = [];
  if (input.actionItems.length > 0) {
    groups.push({ value: "actions", label: "Actions", items: input.actionItems });
  }
  if (input.recentThreadItems.length > 0) {
    groups.push({
      value: "recent-threads",
      label: "Recent Threads",
      items: input.recentThreadItems,
    });
  }
  return groups;
}

function getCommandPaletteInputPlaceholder(mode: CommandPaletteMode): string {
  switch (mode) {
    case "root":
      return "Search commands, projects, and threads...";
    case "submenu":
      return "Search...";
  }
}
